import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { Node, Edge } from "@xyflow/react";
import {
  Send,
  StopCircle,
  ChevronDown,
  ChevronRight,
  RotateCcw,
  MessageSquarePlus,
  Code2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "../../lib/utils";
import { useBuildAgent } from "../../hooks/useBuildAgent";
import { useUndoRedo } from "../../hooks/useUndoRedo";
import { GraphDiffPreview } from "./GraphDiffPreview";
import { CodeDiffPreview, type FileDiffProposal } from "./CodeDiffPreview";
import { FileTree, type FileTreeEntry } from "./FileTree";
import { applyProposal, getConversations, type BuildAgentConversation } from "../../api/buildAgent";
import { queryKeys } from "../../lib/queryKeys";
import { usePractice } from "../../context/PracticeContext";

const MODEL_OPTIONS = [
  { id: "us.anthropic.claude-sonnet-4-20250514-v1:0", label: "Sonnet 4" },
  { id: "us.anthropic.claude-opus-4-20250514-v1:0", label: "Opus 4.6" },
  { id: "us.anthropic.claude-haiku-4-5-20251001-v1:0", label: "Haiku 4.5" },
];

interface BuildAgentPanelProps {
  graphId: string;
  nodes: Node[];
  edges: Edge[];
  setNodes: (nodes: Node[] | ((prev: Node[]) => Node[])) => void;
  setEdges: (edges: Edge[] | ((prev: Edge[]) => Edge[])) => void;
  mode?: "graph" | "workflow" | "code";
  onRulesChanged?: () => void;
}

function ToolCallTrace({ name, input }: { name: string; input: Record<string, unknown> }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="rounded border border-border/50 bg-muted/30 text-[10px] overflow-hidden">
      <button
        type="button"
        className="flex items-center gap-1.5 w-full px-2 py-1 text-left text-muted-foreground hover:text-foreground transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
        <span className="font-mono font-medium">{name}</span>
      </button>
      {expanded && (
        <pre className="px-2 pb-1.5 text-[10px] text-muted-foreground overflow-x-auto">
          {JSON.stringify(input, null, 2)}
        </pre>
      )}
    </div>
  );
}

export function BuildAgentPanel({ graphId, nodes, edges, setNodes, setEdges, mode = "graph", onRulesChanged }: BuildAgentPanelProps) {
  const { selectedPracticeId } = usePractice();
  const companyId = selectedPracticeId ?? "";
  const queryClient = useQueryClient();

  const [input, setInput] = useState("");
  const [selectedModel, setSelectedModel] = useState(
    // Default to Opus for code mode (complex reasoning), Sonnet for others
    mode === "code"
      ? "us.anthropic.claude-opus-4-20250514-v1:0"
      : MODEL_OPTIONS[0].id,
  );
  const [pastConversations, setPastConversations] = useState<BuildAgentConversation[]>([]);
  const [showConvPicker, setShowConvPicker] = useState(false);

  // Code mode: track modified files across proposals in this session
  const [modifiedFiles, setModifiedFiles] = useState<FileTreeEntry[]>([]);
  const [currentBranch, setCurrentBranch] = useState<string>("");

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const agent = useBuildAgent(companyId);
  const undoRedo = useUndoRedo(nodes, edges, setNodes as any, setEdges as any);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [agent.messages]);

  // Load past conversations for this graph
  useEffect(() => {
    if (!companyId) return;
    getConversations(companyId)
      .then((convs) => setPastConversations(convs.filter((c) => c.graphId === graphId)))
      .catch(() => {});
  }, [companyId, graphId]);

  // Detect git_status tool calls to extract branch name
  useEffect(() => {
    for (const msg of agent.messages) {
      if (!msg.toolCalls) continue;
      for (const tc of msg.toolCalls) {
        if (tc.name === "git_status" && tc.input && typeof tc.input === "object") {
          const out = (tc.input as any).output;
          if (typeof out === "string") {
            const match = out.match(/"branch"\s*:\s*"([^"]+)"/);
            if (match) setCurrentBranch(match[1]);
          }
        }
      }
    }
  }, [agent.messages]);

  const handleSend = useCallback(() => {
    const msg = input.trim();
    if (!msg || agent.isStreaming) return;
    setInput("");
    agent.sendMessage(msg, graphId, selectedModel, mode);
  }, [input, agent, graphId, selectedModel, mode]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleAccept = useCallback(
    async (proposalId: string) => {
      try {
        undoRedo.snapshot(); // Save state for undo

        const result = await applyProposal(companyId, agent.conversationId!, proposalId);

        if (result.graphDefinition) {
          // Update ReactFlow canvas from the returned graph definition
          const def = result.graphDefinition as any;
          if (def?.nodes) {
            setNodes(
              def.nodes.map((n: any) => ({
                id: n.id,
                type: n.type,
                position: n.position,
                data: n.data,
              })),
            );
          }
          if (def?.edges) {
            setEdges(
              def.edges.map((e: any) => ({
                id: e.id,
                source: e.source,
                target: e.target,
                label: e.label,
                animated: true,
                style: { stroke: "var(--primary)", strokeWidth: 2 },
                data: { condition: e.condition },
              })),
            );
          }
          void queryClient.invalidateQueries({ queryKey: queryKeys.graphs.detail(graphId) });
        } else if (result.rules) {
          // Workflow rule applied — notify parent to refresh rules list
          onRulesChanged?.();
        }

        agent.markProposal(proposalId, "accepted");
      } catch (err) {
        console.error("Failed to apply proposal:", err);
      }
    },
    [companyId, agent, graphId, setNodes, setEdges, queryClient, undoRedo, onRulesChanged],
  );

  const handleReject = useCallback(
    (proposalId: string) => {
      agent.markProposal(proposalId, "rejected");
    },
    [agent],
  );

  // ── File diff handlers (Phase 5 code mode) ──────────────────────────────────

  const handleFileAccept = useCallback(
    async (proposalId: string, proposal: FileDiffProposal) => {
      try {
        await applyProposal(companyId, agent.conversationId!, proposalId);
        agent.markFileDiff(proposalId, "accepted");

        // Update the local file tree display
        setModifiedFiles((prev) => {
          const existing = prev.find((f) => f.path === proposal.filePath);
          if (existing) return prev; // already tracked
          const status =
            proposal.operationType === "create_file"
              ? "added"
              : proposal.operationType === "delete_file"
                ? "deleted"
                : "modified";
          return [...prev, { path: proposal.filePath, status }];
        });
      } catch (err) {
        console.error("Failed to apply file proposal:", err);
      }
    },
    [companyId, agent],
  );

  const handleFileReject = useCallback(
    (proposalId: string) => {
      agent.markFileDiff(proposalId, "rejected");
    },
    [agent],
  );

  const handleLoadConversation = useCallback(
    (conv: BuildAgentConversation) => {
      agent.loadConversation(
        conv.id,
        conv.messages.map((m) => ({
          id: crypto.randomUUID(),
          role: m.role,
          content: m.content,
          toolCalls: m.toolCalls,
          timestamp: m.timestamp,
        })),
      );
      setShowConvPicker(false);
    },
    [agent],
  );

  const isCodeMode = mode === "code";

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border shrink-0">
        {/* Code mode badge */}
        {isCodeMode && (
          <div className="flex items-center gap-1 text-[10px] text-primary bg-primary/10 rounded px-1.5 py-0.5 font-medium">
            <Code2 className="h-3 w-3" />
            Code
          </div>
        )}

        <select
          value={selectedModel}
          onChange={(e) => setSelectedModel(e.target.value)}
          className="text-[11px] bg-muted border border-border rounded px-2 py-1 text-foreground"
        >
          {MODEL_OPTIONS.map((m) => (
            <option key={m.id} value={m.id}>{m.label}</option>
          ))}
        </select>

        <div className="ml-auto flex items-center gap-1.5">
          {/* Undo / Redo — only relevant in graph/workflow mode */}
          {!isCodeMode && (
            <>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-[11px]"
                disabled={!undoRedo.canUndo}
                onClick={undoRedo.undo}
                title="Undo last AI change"
              >
                ↩ Undo
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 text-[11px]"
                disabled={!undoRedo.canRedo}
                onClick={undoRedo.redo}
                title="Redo"
              >
                ↪ Redo
              </Button>
            </>
          )}

          {/* Past conversations */}
          <div className="relative">
            <Button
              size="sm"
              variant="ghost"
              className="h-7 text-[11px]"
              onClick={() => setShowConvPicker(!showConvPicker)}
              title="Resume a past conversation"
            >
              <MessageSquarePlus className="h-3 w-3 mr-1" />
              History
            </Button>
            {showConvPicker && pastConversations.length > 0 && (
              <div className="absolute right-0 top-full mt-1 w-64 rounded-lg border border-border bg-card shadow-lg z-50 py-1 max-h-48 overflow-y-auto">
                {pastConversations.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => handleLoadConversation(c)}
                    className="w-full text-left px-3 py-2 text-[11px] hover:bg-accent/50 transition-colors"
                  >
                    <div className="font-medium truncate">{c.title ?? "Untitled"}</div>
                    <div className="text-muted-foreground text-[10px]">
                      {new Date(c.updatedAt).toLocaleDateString()}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* New conversation */}
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-[11px]"
            onClick={() => {
              agent.reset();
              setModifiedFiles([]);
              setCurrentBranch("");
            }}
            title="Start a new conversation"
          >
            <RotateCcw className="h-3 w-3" />
          </Button>
        </div>
      </div>

      {/* Code mode: modified files sidebar (shown when there are files) */}
      {isCodeMode && modifiedFiles.length > 0 && (
        <div className="px-3 pt-2 shrink-0">
          <FileTree
            branch={currentBranch || "build-agent/..."}
            files={modifiedFiles}
          />
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-3 min-h-0">
        {agent.messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center gap-2 py-8">
            <p className="text-sm font-medium text-foreground">
              {isCodeMode ? "Code Agent" : "Build Agent"}
            </p>
            {mode === "workflow" ? (
              <>
                <p className="text-xs text-muted-foreground max-w-56 leading-relaxed">
                  Describe the automation rule you want to create. I'll propose it and you approve before it's saved.
                </p>
                <div className="mt-2 space-y-1 text-[11px] text-muted-foreground">
                  <p>"Alert billing when a claim is denied"</p>
                  <p>"Flag critical lab results for immediate review"</p>
                  <p>"What rules do we currently have?"</p>
                </div>
              </>
            ) : isCodeMode ? (
              <>
                <p className="text-xs text-muted-foreground max-w-56 leading-relaxed">
                  Describe a feature or change you want. I'll explore the codebase, propose file changes, and you review each one.
                </p>
                <div className="mt-2 space-y-1 text-[11px] text-muted-foreground">
                  <p>"Add a providers list page"</p>
                  <p>"Add an audit log API endpoint"</p>
                  <p>"What files handle authentication?"</p>
                </div>
              </>
            ) : (
              <>
                <p className="text-xs text-muted-foreground max-w-56 leading-relaxed">
                  Describe what you want to change in natural language. I'll propose the graph modifications and you approve or reject them.
                </p>
                <div className="mt-2 space-y-1 text-[11px] text-muted-foreground">
                  <p>"Add an insurance verification step before scheduling"</p>
                  <p>"Remove the fax notification node"</p>
                  <p>"What does this graph do?"</p>
                </div>
              </>
            )}
          </div>
        )}

        {agent.messages.map((msg) => (
          <div
            key={msg.id}
            className={cn("flex flex-col gap-1", msg.role === "user" ? "items-end" : "items-start")}
          >
            <div
              className={cn(
                "rounded-lg px-3 py-2 text-[12px] max-w-[90%]",
                msg.role === "user"
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted/60 text-foreground",
              )}
            >
              {msg.content || (msg.role === "assistant" && agent.isStreaming ? (
                <span className="flex items-center gap-1 text-muted-foreground">
                  <span className="animate-pulse">●</span>
                  <span className="animate-pulse" style={{ animationDelay: "0.2s" }}>●</span>
                  <span className="animate-pulse" style={{ animationDelay: "0.4s" }}>●</span>
                </span>
              ) : null)}
            </div>

            {/* Tool call traces */}
            {msg.toolCalls && msg.toolCalls.length > 0 && (
              <div className="w-full max-w-[90%] space-y-0.5">
                {msg.toolCalls.map((tc, i) => (
                  <ToolCallTrace key={i} name={tc.name} input={tc.input} />
                ))}
              </div>
            )}

            {/* Graph diff proposal (graph / workflow modes) */}
            {msg.proposal && msg.proposalStatus && (
              <div className="w-full max-w-[90%]">
                <GraphDiffPreview
                  proposal={msg.proposal}
                  status={msg.proposalStatus}
                  onAccept={handleAccept}
                  onReject={handleReject}
                />
              </div>
            )}

            {/* File diff proposal (code mode — Phase 5) */}
            {msg.fileDiffProposal && msg.fileDiffStatus && (
              <div className="w-full max-w-[90%]">
                <CodeDiffPreview
                  proposal={msg.fileDiffProposal}
                  accepted={msg.fileDiffStatus === "accepted"}
                  rejected={msg.fileDiffStatus === "rejected"}
                  onAccept={(proposalId) =>
                    handleFileAccept(proposalId, msg.fileDiffProposal!)
                  }
                  onReject={handleFileReject}
                />
              </div>
            )}
          </div>
        ))}

        {agent.error && (
          <div className="text-[11px] text-destructive bg-destructive/10 rounded px-3 py-2">
            {agent.error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="px-3 py-2 border-t border-border shrink-0">
        <div className="flex items-end gap-2">
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isCodeMode
                ? "Describe a feature or change to make..."
                : "Describe what you want to change..."
            }
            rows={2}
            className="flex-1 resize-none rounded-md border border-border bg-muted/30 px-3 py-2 text-[12px] placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/50"
            disabled={agent.isStreaming}
          />
          {agent.isStreaming ? (
            <Button size="sm" variant="outline" className="h-9 shrink-0" onClick={agent.stop}>
              <StopCircle className="h-3.5 w-3.5" />
            </Button>
          ) : (
            <Button
              size="sm"
              className="h-9 shrink-0"
              onClick={handleSend}
              disabled={!input.trim()}
            >
              <Send className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
        <p className="mt-1 text-[10px] text-muted-foreground">Enter to send · Shift+Enter for newline</p>
      </div>
    </div>
  );
}
